import { FlexTableBox } from '../FlexTableBox';
import {
  CompactGridWrapper,
  CompactGridWrapperProps,
  SAMPLE_AND_PATIENT_IDENTIFYING_COLUMNS,
} from './CompactGridWrapper';
import { GridColDef, GridColType } from '@mui/x-data-grid-pro';
import { Divider } from '@mui/material';
import React, { MutableRefObject, useEffect, useMemo, useState, useCallback, SetStateAction } from 'react';
import { AuthorizedSection } from '../../auth/AuthorizedSection';
import { ExportSelectorDialog } from '../ExportSelectorDialog';
import { MemoizedColumnSelectorDialog } from '../TableLineageColumnSelectorDialog';
import { FlexBox } from '../FlexBox';
import {
  SelectableCandidate,
  SelectableEntity,
  SelectableFieldGridColTypeMapper,
  SelectedData,
  SelectFieldsGroupedByProject,
} from '../../data/SampleTrackingFieldSelectionData';
import { LoadingProps, LoadState } from '../LoadingStateUtil';
import useAuth from '../../auth/UseAuth';
import { difference, filter, find, flatMap, map } from 'lodash';
import { useParams } from 'react-router-dom';
import { formatDate, jsonIsEqual } from '../../util/grid/TableUtils';
import useLocalStorage from '../../hooks/UseLocalStorage';
import { GridColumnVisibilityModel } from '@mui/x-data-grid/hooks/features/columns/gridColumnsInterfaces';
import { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro';
import { useResearchProject } from '../hooks/UseResearchProjects';
import { v4 as uuidv4 } from 'uuid';

export interface SampleTrackingGridProps extends CompactGridWrapperProps {
  rightActions?: React.ReactNode;
  leftActions?: React.ReactNode;
  loadingProps: LoadingProps;
  cacheKey: string;
  defaultColumnOrdering?: ReadonlyArray<string>;
  columnDefinitionOverrides?: ReadonlyArray<GridColDef>;
  apiRef: MutableRefObject<GridApiPro>;
  refreshTrigger?: boolean;
  columnsToAutoSizeOverride?: ReadonlyArray<string>;
  sampleHierarchyDialog?: React.ReactNode;
  configuredTransitionId?: string;
  transitionEnumId?: string;
}

const CURRENT_VIEW_ENTITY = 'current_view';

export const SampleTrackingGridWrapper = (props: SampleTrackingGridProps) => {
  const [loadingState, setLoadingState] = props.loadingProps;

  const { accessToken } = useAuth();
  let { researchProjectId } = useParams() as { researchProjectId: string };
  const researchProject = useResearchProject(researchProjectId, setLoadingState);

  const [refreshTrigger, setRefreshTrigger] = useState<boolean>(true);

  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [orderedColumns, setOrderedColumns] = useState<GridColDef[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});
  const [hasCustomViewApplied, setHasCustomViewApplied] = useState<boolean>(false);

  const [selectableCandidates, setSelectableCandidates] = useState<ReadonlyArray<SelectableCandidate>>([]);
  const [currentSelectedColumnEntities, setCurrentSelectedColumnEntities] = useState<ReadonlyArray<SelectableEntity>>(
    []
  );
  const [allColumnEntities, setAllColumnEntities] = useState<ReadonlyArray<SelectableEntity>>([]);
  const [additionalColumnData, setAdditionalColumnData] = useState<SelectedData>({
    data: {},
    columns: [],
    entries: [],
  });
  const [entitiesFromProvidedColumns, setEntitiesFromProvidedColumns] = useState<SelectableEntity[]>([]);

  const [columnOrdering, setColumnOrdering] = useLocalStorage<ReadonlyArray<String>>(
    `${props.cacheKey}-column-ordering`,
    props.defaultColumnOrdering ?? []
  );

  useEffect(() => {
    return LoadState(setLoadingState, async () => {
      if (!accessToken || currentSelectedColumnEntities.length === 0 || selectableCandidates.length === 0) {
        return;
      }

      const data = await SelectFieldsGroupedByProject(
        selectableCandidates,
        currentSelectedColumnEntities,
        researchProjectId,
        accessToken
      );

      setAdditionalColumnData(data);
    });
  }, [
    accessToken,
    currentSelectedColumnEntities,
    researchProjectId,
    selectableCandidates,
    setLoadingState,
    refreshTrigger,
  ]);

  useEffect(() => {
    // If no columns are selected then clear table data
    if (!(currentSelectedColumnEntities.length === 0 && additionalColumnData.entries.length > 0)) {
      return;
    }

    setAdditionalColumnData({
      data: {},
      columns: [],
      entries: [],
    });
  }, [currentSelectedColumnEntities, additionalColumnData]);

  const selectableCandidatesFromRows = useMemo(() => {
    if (props.rows.length === 0) return [];

    const candidateMap = new Map<string, SelectableCandidate>();

    props.rows.forEach(row => {
      if (!row.sampleId) return;
      const projectId = row.researchProjectId ?? researchProjectId;
      const key = `${row.sampleId}:${projectId}`;

      if (!candidateMap.has(key)) {
        candidateMap.set(key, {
          sampleId: row.sampleId,
          entryId: uuidv4(),
          researchProjectId: projectId,
        });
      }
    });

    return Array.from(candidateMap.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.rows.length, researchProjectId]);

  useEffect(() => {
    if (selectableCandidatesFromRows.length === 0) return;

    setSelectableCandidates(prevState => {
      const newState = selectableCandidatesFromRows.map(candidate => {
        const existing = prevState.find(
          c => c.sampleId === candidate.sampleId && c.researchProjectId === candidate.researchProjectId
        );
        return existing ? existing : candidate;
      });

      if (jsonIsEqual(prevState, newState)) {
        return prevState;
      }

      return newState;
    });
  }, [selectableCandidatesFromRows]);

  useEffect(() => {
    if (hasCustomViewApplied) {
      return;
    }

    const allFields = flatMap(allColumnEntities, i => i.fields);

    const currentViewEntity =
      currentSelectedColumnEntities.find(i => i.name === CURRENT_VIEW_ENTITY) ||
      entitiesFromProvidedColumns.find(i => i.name === CURRENT_VIEW_ENTITY);
    const currentViewFieldNames = currentViewEntity?.fields.map(i => i.key) ?? [];

    const visibility: GridColumnVisibilityModel = {};

    allFields.forEach(col => {
      visibility[col.key] = false;
    });

    additionalColumnData.columns.forEach(col => {
      visibility[col.key] = true;
    });

    props.columns.forEach(col => {
      visibility[col.field] = currentSelectedColumnEntities.length > 0 && currentViewFieldNames.includes(col.field);
    });

    setColumnVisibilityModel(prevState => {
      if (jsonIsEqual(prevState, visibility)) {
        return prevState;
      }
      return visibility;
    });
  }, [
    props.columns,
    allColumnEntities,
    currentSelectedColumnEntities,
    additionalColumnData,
    entitiesFromProvidedColumns,
    hasCustomViewApplied,
  ]);

  useEffect(() => {
    const allFields = flatMap(allColumnEntities, i => i.fields);

    const propColumnFields = map(props.columns, i => i.field);

    setColumns(prevState => {
      const newColumns = [
        ...props.columns,
        ...(map(
          filter(allFields, i => !propColumnFields.includes(i.key)),
          column => {
            const override = find(props.columnDefinitionOverrides, i => i.field === column.key);
            const columnType = SelectableFieldGridColTypeMapper[column.type];

            return override !== undefined
              ? {
                  ...override,
                  headerName: column.fullDisplayName,
                  valueGetter: ({ row }) => (row?.additionalData && row?.additionalData[column.key]) ?? '',
                }
              : {
                  field: column.key,
                  headerName: column.fullDisplayName,
                  headerAlign: GetColumnHeaderAlignment(columnType),
                  align: GetColumnAlignment(columnType),
                  sortable: true,
                  minWidth: 130,
                  type: columnType,
                  valueGetter: ({ row }) => {
                    if (row?.additionalData && row?.additionalData[column.key]) {
                      const rowValue = row?.additionalData[column.key];
                      return column.type === 'date' || column.type === 'dateTime' ? new Date(rowValue) : rowValue;
                    }
                  },
                  valueFormatter: ({ value }) =>
                    (column.type === 'date' || column.type === 'dateTime') && value ? formatDate(value) : value,
                };
          }
        ) as GridColDef[]),
      ];

      if (jsonIsEqual(prevState, newColumns)) {
        return prevState;
      }

      return newColumns;
    });
  }, [props.columns, props.columnDefinitionOverrides, allColumnEntities]);

  useEffect(() => {
    setRows(
      props.rows.map(row => {
        const rowProjectId = row.researchProjectId ?? researchProjectId;
        const sampleId = row.sampleId;

        const entry = additionalColumnData.entries.find(
          e => e.sampleId === sampleId && e.researchProjectId === rowProjectId
        );

        const additionalData = entry ? additionalColumnData.data[entry.entryId] ?? {} : {};

        return { additionalData, ...row };
      })
    );
  }, [props.rows, additionalColumnData, researchProjectId]);

  useEffect(() => {
    props.apiRef?.current?.subscribeEvent('columnOrderChange', () => {
      if (!props.apiRef?.current) {
        return;
      }

      const order = props.apiRef.current.getVisibleColumns().map(i => i.field);
      setColumnOrdering(order);
    });
  }, [props.apiRef, setColumnOrdering]);

  useEffect(() => {
    const orderedColumns = filter(
      map(columnOrdering, column => {
        return find(columns, col => col.field === column);
      }),
      col => col !== undefined && col !== null
    ) as GridColDef[];

    const orderedColumnsFields = orderedColumns.map(col => col?.field);
    const remainingColumns = columns.filter(col => !orderedColumnsFields.includes(col.field));
    const newColumns = [...orderedColumns, ...remainingColumns];

    setOrderedColumns(prevState => {
      if (jsonIsEqual(prevState, newColumns)) {
        return prevState;
      }

      return newColumns;
    });
  }, [columnOrdering, columns]);

  useEffect(() => {
    setEntitiesFromProvidedColumns([
      {
        name: CURRENT_VIEW_ENTITY,
        displayName: 'Current View',
        fields: props.columns
          .filter(col => col.headerName !== undefined)
          .map(col => ({
            columnDisplayName: col.headerName ?? col.field,
            columnName: col.field,
            fullDisplayName: col.headerName ?? col.field,
            key: col.field,
            type: 'text',
          })),
      },
    ]);
  }, [props.columns]);

  useEffect(() => {
    setRefreshTrigger(current => !current);
  }, [props.refreshTrigger]);

  useEffect(() => {
    if (!props.apiRef?.current || !props.initialState) {
      return;
    }

    // Restore initial state once all columns are loaded otherwise grid disposes of initial state
    // whenever new columns are added. Columns should be only trigger for this
    props.apiRef?.current?.restoreState(props.initialState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  // Update selected columns/entities from a custom view
  const handleApplyViewColumns = useCallback(
    (fields: string[]) => {
      const selectedEntities = allColumnEntities
        .map(entity => ({
          ...entity,
          fields: entity.fields.filter(f => fields.includes(f.key)),
        }))
        .filter(entity => entity.fields.length > 0);
      setCurrentSelectedColumnEntities(selectedEntities);
    },
    [allColumnEntities]
  );

  return (
    <FlexTableBox>
      <FlexBox>
        <FlexBox width={'100%'} flexDirection='row' alignItems='center' justifyContent='space-between' mb={1}>
          <>{props.leftActions}</>
          <FlexBox flexDirection='row' alignItems='center' justifyContent='space-between'>
            <AuthorizedSection hasSampleTrackingViewAccess>
              <ExportSelectorDialog
                candidates={selectableCandidates}
                apiRef={props.apiRef}
                fileName={researchProject ? researchProject.name : undefined}
                researchProjectId={researchProjectId}
                currentEntitiesInView={currentSelectedColumnEntities}
              />
              <MemoizedColumnSelectorDialog
                setAdditionalColumnEntities={useCallback(
                  (entities: SetStateAction<ReadonlyArray<SelectableEntity>>) => {
                    setCurrentSelectedColumnEntities(entities);
                    // Reset custom view flag when user changes column selection
                    setHasCustomViewApplied(false);
                  },
                  []
                )}
                setAllColumnEntities={setAllColumnEntities}
                defaultColumns={props.defaultColumnOrdering ?? []}
                entitiesFromProvidedColumns={entitiesFromProvidedColumns}
                cacheKey={props.cacheKey}
                selectedEntities={currentSelectedColumnEntities}
              />
              {props.sampleHierarchyDialog}
              <Divider
                orientation='vertical'
                variant='middle'
                flexItem
                sx={{ width: 2, bgcolor: 'secondary.light', marginLeft: 1 }}
              />
            </AuthorizedSection>
            <>{props.rightActions}</>
          </FlexBox>
        </FlexBox>
      </FlexBox>
      <CompactGridWrapper
        {...props}
        rows={rows}
        columns={orderedColumns}
        loadingState={loadingState}
        columnsToAutoSize={
          props.columnsToAutoSize || additionalColumnData.columns.length > 0
            ? difference(
                orderedColumns.map(c => c.field).concat(props.columnsToAutoSize ?? []),
                SAMPLE_AND_PATIENT_IDENTIFYING_COLUMNS
              )
            : undefined
        }
        disableColumnSelector
        columnVisibilityModel={columnVisibilityModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        setHasCustomViewApplied={setHasCustomViewApplied}
        onApplyViewColumns={handleApplyViewColumns}
      />
    </FlexTableBox>
  );
};

function GetColumnHeaderAlignment(columnType: GridColType) {
  switch (columnType) {
    case 'date':
    case 'dateTime':
    case 'number':
      return 'center';
    default:
      return 'left';
  }
}

function GetColumnAlignment(columnType: GridColType) {
  switch (columnType) {
    case 'date':
    case 'dateTime':
      return 'center';
    case 'number':
      return 'right';
    default:
      return 'left';
  }
}
